
import { Options, Vue } from "vue-class-component";
import EmptyState from "@/app/ui/components/empty-list/index.vue";
import {
  convertDecimalWithComma,
  formatDate,
} from "@/app/infrastructures/misc/Utils";
import formatPriceRP from "@/app/infrastructures/misc/common-library/v2/FormatPriceRP";
import FilterByDate from "@/app/ui/views/shipment/cn-manifest/component/filter-by-date.vue";
import FilterByStt from "@/app/ui/views/shipment/cn-manifest/component/filter-by-stt.vue";
import { CnManifestController } from "@/app/ui/controllers/CnManifestController";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { MainAppController } from "@/app/ui/controllers/MainAppController";

@Options({
  components: {
    EmptyState,
    FilterByDate,
    FilterByStt,
  },
})
export default class CnManifest extends Vue {
  created() {
    CnManifestController.initCnManifestFilter();
    CnManifestController.initCnManifests();
  }
  get getCurrentRoute() {
    return this.$route.meta.name;
  }
  get filter() {
    return CnManifestController.cnManifestFilter;
  }
  get cacheFilter() {
    return CnManifestController.cacheCnManifestFilter;
  }
  get isDisabledSearch() {
    if (this.filter.sttListingType === "stt_list") {
      return this.filter.sttNumbers.length === 0;
    }
    if (this.filter.sttListingType === "booking_date") {
      return (
        this.filter.clientPartner === "" ||
        this.filter.destinationCity === "" ||
        this.filter.startDate === "" ||
        this.filter.endDate === ""
      );
    }
    return true;
  }

  // handle toggle filter
  isShowFilter = true;
  setIsShowFilter(val: boolean) {
    this.isShowFilter = val;
  }

  // Table
  get isEmpty() {
    return (
      this.cnManifests.data.length === 0 && this.firstRequest && !this.isLoading
    );
  }
  get isRefreshIcon() {
    return (
      !this.firstRequest &&
      !this.isLoading &&
      JSON.stringify(this.filter.sttNumbers) !==
        JSON.stringify(this.cacheFilter.sttNumbers)
    );
  }
  get firstRequest() {
    return CnManifestController.firstRequest;
  }
  get isError() {
    return CnManifestController.isError;
  }
  get isLoading() {
    return CnManifestController.loadingCnManifests;
  }
  get errorCause() {
    return CnManifestController.errorCause;
  }
  get cnManifests() {
    return CnManifestController.cnManifests;
  }
  isAccountCountryOutsideId(bookedBy: string) {
    if (AccountController.accountData.isInternalAccount) {
      return bookedBy === "MY"
    }
    return (
      AccountController.accountData.account_type_detail.countryCode?.toLocaleLowerCase() !==
      "id"
    );
  }
  get columns() { 
    return [
    {
      name: this.$t("cnManifest.columnNo"),
      styleHead: "w-24 text-left whitespace-no-wrap",
      render: (item: any, index: number) => {
        return `<div class="text-black-lp-300">${index + 1}</div>`;
      },
    },
    {
      name: this.$t("cnManifest.columnBookingDate"),
      styleHead: "w-60 text-left whitespace-no-wrap",
      render: (item: any) => {
        return `<div class="text-black-lp-300">${formatDate(
          item.sttCreatedAt
        )}</div>`;
      },
    },
    {
      name: this.$t("cnManifest.columnNoStt"),
      styleHead: "w-50 text-left whitespace-no-wrap",
      render: (item: any) => {
        return `<div class="text-black-lp-300">${item.sttNo}</div>`;
      },
    },
    {
      name: this.$t("cnManifest.columnDestinationCity"),
      styleHead: "w-40 text-left whitespace-no-wrap",
      render: (item: any) => {
        return `<div class='text-black-lp-300'>${item.sttDestinationCity}</div>`;
      },
    },
    {
      name: this.$t("cnManifest.columnTotalPiece"),
      styleHead: "w-32 text-left whitespace-no-wrap",
      render: (item: any) => {
        return `<div class='text-black-lp-300'>${item.sttTotalPiece}</div>`;
      },
    },
    {
      name: this.$t("cnManifest.columnTotalWeight"),
      styleHead: "w-40 text-left whitespace-no-wrap",
      render: (item: any) => {
        return `<div class='text-black-lp-300'>${convertDecimalWithComma(
          item.sttChargeAbleWeight,
          2
        )} Kg</div>`;
      },
    },
    {
      name: this.$t("cnManifest.columnTotalVolume"),
      styleHead: "w-40 text-left whitespace-no-wrap",
      render: (item: any) => {
        return `<div class='text-black-lp-300'>${convertDecimalWithComma(
          item.sttVolumeWeight,
          2
        )}</div>`;
      },
    },
    {
      name: this.$t("cnManifest.columnProduct"),
      styleHead: "w-40 text-left",
      render: (item: any) => {
        return `<div class="text-black-lp-300 flex">
                  <div class="rounded px-2 py-0 bg-gray-lp-400">
                      ${item.sttProductType}
                  </div>
                </div>`;
      },
    },
    {
      name: this.$t("cnManifest.columnRecipient"),
      styleHead: "w-40 text-left",
      render: (item: any) => {
        return `<div class='text-black-lp-300'>${item.sttRecipientName}</div>`;
      },
    },
    {
      name: this.$t("cnManifest.columnRecipientAddress"),
      styleHead: "w-64 text-left",
      render: (item: any) => {
        return `<div class='text-black-lp-300'>${item.sttRecipientAddress}</div>`;
      },
    },
    {
      name: this.$t("cnManifest.columnRecipientPhone"),
      styleHead: "w-60 text-left whitespace-no-wrap",
      render: (item: any) => {
        return `<div class='text-black-lp-300'>${item.sttRecipientPhone}</div>`;
      },
    },
    {
      name: this.$t("cnManifest.columnCommodityType"),
      styleHead: "w-40 text-left",
      render: (item: any) => {
        return `<div class='text-black-lp-300'>${item.sttCommodityName}</div>`;
      },
    },
    {
      name: this.$t("cnManifest.columnHsCode"),
      styleHead: "w-40 text-left whitespace-no-wrap",
      render: (item: any) => {
        return `<div class='text-black-lp-300'>${item.sttHsCode}</div>`;
      },
    },
    {
      name: this.$t("cnManifest.columnNpwp"),
      styleHead: "w-56 text-left whitespace-no-wrap",
      render: (item: any) => {
        return `<div class='text-black-lp-300'>${item.sttTaxNumber}</div>`;
      },
    },
    {
      name: this.$t("cnManifest.columnItemPrice"),
      styleHead: "w-40 text-left whitespace-no-wrap",
      render: (item: any) => {
        return `<div class='text-black-lp-300'>${formatPriceRP(item.sttGoodsEstimatePrice, this.isAccountCountryOutsideId(item.sttBookedByCountry) ? "MYR" : "IDR")}</div>`;
      },
    },
    {
      name: this.$t("cnManifest.columnBm"),
      styleHead: "w-24 text-left whitespace-no-wrap",
      render: (item: any) => {
        return `<div class='text-black-lp-300'>${item.sttTaxStamp.toLocaleString(
          "id"
        )}</div>`;
      },
    },
    {
      name: this.$t("cnManifest.columnPpn"),
      styleHead: "w-24 text-left whitespace-no-wrap",
      render: (item: any) => {
        return `<div class='text-black-lp-300'>${item.sttValueAddedTax.toLocaleString(
          "id"
        )}</div>`;
      },
    },
    {
      name: this.$t("cnManifest.columnPph"),
      styleHead: "w-24 text-left whitespace-no-wrap",
      render: (item: any) => {
        return `<div class='text-black-lp-300'>${item.sttIncomeTax.toLocaleString(
          "id"
        )}</div>`;
      },
    },
    {
      name: this.$t("cnManifest.columnTotalGoodTax"),
      styleHead: "w-40 text-left whitespace-no-wrap",
      render: (item: any) => {
        return `<div class='text-black-lp-300'>${item.sttTotalGoodTax.toLocaleString(
          "id"
        )}</div>`;
      },
    },
    {
      name: this.$t("cnManifest.columnTaxImposition"),
      styleHead: "w-40 text-left whitespace-no-wrap",
      render: (item: any) => {
        return `<div class='text-black-lp-300'>${item.sttTaxImposition}</div>`;
      },
    },
    {
      name: this.$t("cnManifest.columnStatus"),
      styleHead: "w-40 text-left",
      render: (item: any) => {
        return `<div class="text-black-lp-300 flex">
                  <div class="rounded-full capitalize px-2 py-0 bg-gray-lp-400">
                      ${item.sttLastStatus}
                  </div>
                </div>`;
      },
      toolTip: (item: any) => {
        return `<div class='flex text-black-lp-300'>${item.sttLastStatusDescription}</div>`;
      },
    },
    {
      name: this.$t("cnManifest.columnBookedBy"),
      styleHead: "w-40 text-left whitespace-no-wrap",
      render: (item: any) => {
        return `<div class='text-black-lp-300'>${item.sttBookedBy}</div>`;
      },
    },
    {
      name: this.$t("cnManifest.columnTotalAmount"),
      styleHead: "w-40 text-left whitespace-no-wrap",
      render: (item: any) => {
        return `<div class='text-black-lp-300'>${formatPriceRP(
          item.sttTotalAmount, this.isAccountCountryOutsideId(item.sttBookedByCountry) ? "MYR" : "IDR"
        )}</div>`;
      },
    },
  ]}
  onRequest() {
    if (this.isDisabledSearch) {
      return;
    }
    CnManifestController.fetchCnManifests();
  }
  exportToCsv() {
    CnManifestController.exportManifestsToCsv();
  }

  // STT Listing
  sttType = false;
  get sttTypeOptions() { 
    return [
    {
      name: this.$t("cnManifest.sttListingTypeBookingDate"),
      value: "booking_date",
    },
    {
      name: this.$t("cnManifest.sttListingTypeSttList"),
      value: "stt_list",
    },
  ]}
  get sttListingType() {
    return CnManifestController.cnManifestFilter.sttListingType;
  }
  onSelectSttType(name: string, value: string) {
    if (this.sttListingType !== value) {
      CnManifestController.initCnManifestFilter();
      CnManifestController.initCnManifests();
      CnManifestController.setCnManifestFilter({
        key: "sttListingType",
        value: value,
      });
    }
    this.onCloseSttType();
  }
  onOpenSttType() {
    this.sttType = true;
  }
  onCloseSttType() {
    this.sttType = false;
  }
  get isSidebarAndBreadcrumbV2() {
    return MainAppController.isSidebarAndBreadcrumbV2;
  }
}
